import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import { insightsService } from '../../shared/services/insights.service';

/**
 * Admin-only dashboard surfacing two demand signals:
 *
 *  • Top cities — what people are looking for, and where we're empty-handed.
 *    Cities with a high {@code zeroResultSearches} rate are the prime
 *    targets for provider recruitment.
 *  • Top categories — which services are most in demand, and which of those
 *    repeatedly come up empty.
 */
const WINDOW_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 365, label: '1y' },
] as const;

export function AdminInsightsPage() {
  const { t } = useTranslation();
  const [windowDays, setWindowDays] = useState<number>(30);

  const summary = useQuery({
    queryKey: ['admin', 'insights', 'search', windowDays],
    queryFn: () => insightsService.searchSummary(windowDays, 20),
    staleTime: 60 * 1000,
  });

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <TrendingUpRoundedIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {t('admin.insights.title')}
          </Typography>
          <Typography color="text.secondary">
            {t('admin.insights.subtitle')}
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          value={windowDays}
          onChange={(e) => setWindowDays(Number(e.target.value))}
          sx={{ minWidth: 110 }}
          label={t('admin.insights.window')}
        >
          {WINDOW_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {summary.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !summary.data ? null : (
        <>
          {/* Headline counters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <KpiCard
                label={t('admin.insights.totalSearches')}
                value={summary.data.totalSearches.toLocaleString()}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <KpiCard
                label={t('admin.insights.zeroResultSearches')}
                value={summary.data.zeroResultSearches.toLocaleString()}
                hint={t('admin.insights.zeroResultHint')}
                tone="warning"
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {/* Top cities */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ borderRadius: 3 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <LocationOnRoundedIcon color="primary" />
                  <Typography variant="h6">
                    {t('admin.insights.topCitiesTitle')}
                  </Typography>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('admin.insights.colCity')}</TableCell>
                        <TableCell align="right">
                          {t('admin.insights.colSearches')}
                        </TableCell>
                        <TableCell align="right">
                          {t('admin.insights.colZero')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.data.topCities.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography color="text.secondary" variant="body2">
                              {t('admin.insights.empty')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        summary.data.topCities.map((row) => (
                          <TableRow key={row.city}>
                            <TableCell sx={{ textTransform: 'capitalize' }}>
                              {row.city}
                            </TableCell>
                            <TableCell align="right">{row.totalSearches}</TableCell>
                            <TableCell align="right">
                              {row.zeroResultSearches > 0 ? (
                                <Chip
                                  label={row.zeroResultSearches}
                                  size="small"
                                  color="warning"
                                  sx={{ height: 22 }}
                                />
                              ) : (
                                row.zeroResultSearches
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Top categories */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ borderRadius: 3 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <CategoryRoundedIcon color="primary" />
                  <Typography variant="h6">
                    {t('admin.insights.topCategoriesTitle')}
                  </Typography>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('admin.insights.colCategory')}</TableCell>
                        <TableCell align="right">
                          {t('admin.insights.colSearches')}
                        </TableCell>
                        <TableCell align="right">
                          {t('admin.insights.colZero')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.data.topCategories.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography color="text.secondary" variant="body2">
                              {t('admin.insights.empty')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        summary.data.topCategories.map((row) => (
                          <TableRow key={row.categoryId}>
                            <TableCell>
                              {row.categoryName ?? row.categoryCode ?? '—'}
                            </TableCell>
                            <TableCell align="right">{row.totalSearches}</TableCell>
                            <TableCell align="right">
                              {row.zeroResultSearches > 0 ? (
                                <Chip
                                  label={row.zeroResultSearches}
                                  size="small"
                                  color="warning"
                                  sx={{ height: 22 }}
                                />
                              ) : (
                                row.zeroResultSearches
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
}

function KpiCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'warning';
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        borderColor: tone === 'warning' ? 'warning.main' : 'divider',
        bgcolor: (theme) =>
          tone === 'warning'
            ? theme.palette.mode === 'dark'
              ? 'rgba(245,181,67,0.10)'
              : 'rgba(245,127,23,0.06)'
            : 'background.paper',
      }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Typography variant="h3" sx={{ mt: 0.5, mb: hint ? 0.5 : 0, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
}
